import { z } from "zod";

export const MAX_PRINT_FONT_BYTES = 2 * 1024 * 1024;
export const MAX_PRINT_FONT_PACKAGE_BYTES = 16 * 1024 * 1024;
const pct = String.raw`(?:[0-9]{1,4}(?:\.[0-9]{1,4})?)%`;
const featureTag = String.raw`"[A-Za-z0-9 ]{4}"`;
/** Optional face descriptors. Each grammar is closed (no free CSS) so a value
 * can never close the rule or add declarations. Absent means the CSS default. */
const faceDescriptors = {
  sizeAdjust: z
    .string()
    .regex(new RegExp(`^${pct}$`))
    .refine((value) => parseFloat(value) > 0)
    .optional(),
  ascentOverride: z.string().regex(new RegExp(`^(?:normal|${pct})$`)).optional(),
  descentOverride: z.string().regex(new RegExp(`^(?:normal|${pct})$`)).optional(),
  lineGapOverride: z.string().regex(new RegExp(`^(?:normal|${pct})$`)).optional(),
  featureSettings: z
    .string()
    .max(1000)
    .regex(new RegExp(`^${featureTag}(?: (?:on|off|[0-9]{1,5}))?(?:, ${featureTag}(?: (?:on|off|[0-9]{1,5}))?){0,63}$`))
    .optional(),
  variationSettings: z
    .string()
    .max(1000)
    .regex(new RegExp(`^${featureTag} -?[0-9]{1,5}(?:\\.[0-9]{1,4})?(?:, ${featureTag} -?[0-9]{1,5}(?:\\.[0-9]{1,4})?){0,63}$`))
    .optional(),
};
const descriptorCss: [keyof typeof faceDescriptors, string][] = [
  ["sizeAdjust", "size-adjust"],
  ["ascentOverride", "ascent-override"],
  ["descentOverride", "descent-override"],
  ["lineGapOverride", "line-gap-override"],
  ["featureSettings", "font-feature-settings"],
  ["variationSettings", "font-variation-settings"],
];
export const printFontSchema = z
  .object({
    family: z
      .string()
      .trim()
      .min(1)
      .max(200)
      .regex(/^[^<>\\\u0000-\u001f]+$/),
    weight: z
      .string()
      .regex(/^(normal|bold|[1-9][0-9]{0,2}|1000)( ([1-9][0-9]{0,2}|1000))?$/),
    style: z.enum(["normal", "italic", "oblique"]),
    stretch: z
      .string()
      .regex(/^(normal|[1-9][0-9]{0,2}%)( [1-9][0-9]{0,2}%)?$/),
    unicodeRange: z
      .string()
      .min(1)
      .max(10000)
      .regex(
        /^U\+[0-9A-F?]{1,6}(-[0-9A-F]{1,6})?(\s*,\s*U\+[0-9A-F?]{1,6}(-[0-9A-F]{1,6})?)*$/i,
      ),
    format: z.enum(["woff2", "woff", "truetype", "opentype"]),
    data: z
      .string()
      .min(8)
      .max(Math.ceil(MAX_PRINT_FONT_BYTES / 3) * 4)
      .regex(
        /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/,
      ),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    ...faceDescriptors,
  })
  .strict();
export const printFontMetadataSchema = printFontSchema.omit({ data: true });
export type PrintFontResource = z.infer<typeof printFontSchema>;

export function decodePrintFont(font: PrintFontResource): Uint8Array {
  const text = atob(font.data);
  if (btoa(text) !== font.data || text.length > MAX_PRINT_FONT_BYTES)
    throw new Error(
      "Print font encoding is not canonical or exceeds its size limit",
    );
  const signature = text.slice(0, 4);
  const expected = {
    woff2: "wOF2",
    woff: "wOFF",
    truetype: "\u0000\u0001\u0000\u0000",
    opentype: "OTTO",
  }[font.format];
  if (signature !== expected)
    throw new Error("Print font bytes do not match their declared format");
  return Uint8Array.from(text, (c) => c.charCodeAt(0));
}

export function normalizePrintFonts(
  raw: PrintFontResource[],
): PrintFontResource[] {
  const fonts = new Map<string, PrintFontResource>();
  let total = 0;
  for (const item of raw) {
    const font = printFontSchema.parse(item);
    // Descriptors join identity only when present, so descriptor-free faces
    // keep their previous identity and old receipts normalize unchanged.
    const extra = descriptorCss.flatMap(([key]) =>
      font[key] === undefined ? [] : [[key, font[key]]],
    );
    const identity = JSON.stringify([
      font.family.toLowerCase(),
      font.weight,
      font.style,
      font.stretch,
      font.unicodeRange.toUpperCase(),
      ...(extra.length ? [extra] : []),
    ]);
    const old = fonts.get(identity);
    if (
      old &&
      (old.sha256 !== font.sha256 ||
        old.data !== font.data ||
        old.format !== font.format)
    )
      throw new Error(`Conflicting print font resource for ${font.family}`);
    if (old) continue;
    total += decodePrintFont(font).byteLength;
    if (total > MAX_PRINT_FONT_PACKAGE_BYTES)
      throw new Error("Print font package exceeds its size limit");
    fonts.set(identity, font);
    if (fonts.size > 256) throw new Error("Print font count exceeds its limit");
  }
  return [...fonts.values()];
}

export function printFontsCss(fonts: PrintFontResource[]): string {
  return normalizePrintFonts(fonts)
    .map((font) => {
      const mime = {
        woff2: "font/woff2",
        woff: "font/woff",
        truetype: "font/ttf",
        opentype: "font/otf",
      }[font.format];
      return `@font-face{font-family:${JSON.stringify(font.family)};font-weight:${font.weight};font-style:${font.style};font-stretch:${font.stretch};unicode-range:${font.unicodeRange};${descriptorCss
        .flatMap(([key, css]) => (font[key] === undefined ? [] : [`${css}:${font[key]};`]))
        .join("")}font-display:block;src:url("data:${mime};base64,${font.data}") format("${font.format}");}`;
    })
    .join("\n");
}
